import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    fetchCustomerBills,
    fetchCustomerBillsInfo,
    fetchCustomerCommissionBills,
    fetchCustomerCommissionBillsInfo,
    fetchCustomerDownloads,
    fetchCustomerDownloadTopics,
    fetchCustomerLocalizedDownloads,
    fetchCustomerLocalizedDownloadsWithLandingpage,
} from 'src/api/customer/downloads';
import {
    FETCH_CUSTOMER_BILLS,
    FETCH_CUSTOMER_BILLS_INFO,
    FETCH_CUSTOMER_COMMISSION_BILLS,
    FETCH_CUSTOMER_COMMISSION_BILLS_INFO,
    FETCH_CUSTOMER_DOWNLOADS,
    FETCH_CUSTOMER_DOWNLOAD_TOPICS,
    requestCustomerBills,
    requestCustomerBillsFailure,
    requestCustomerBillsInfo,
    requestCustomerBillsInfoFailure,
    requestCustomerBillsInfoSuccess,
    requestCustomerBillsSuccess,
    requestCustomerCommissionBills,
    requestCustomerCommissionBillsFailure,
    requestCustomerCommissionBillsInfo,
    requestCustomerCommissionBillsInfoFailure,
    requestCustomerCommissionBillsInfoSuccess,
    requestCustomerCommissionBillsSuccess,
    requestCustomerDownloads,
    requestCustomerDownloadsFailure,
    requestCustomerDownloadsSuccess,
    requestCustomerDownloadTopics,
    requestCustomerDownloadTopicsFailure,
    requestCustomerDownloadTopicsSuccess,
} from './actions';

/* Customer Download Topics */
function* fetchCustomerDownloadTopicsSaga(action: AnyAction): SagaIterator {
    yield put(requestCustomerDownloadTopics());

    const { error, response } = yield call(fetchCustomerDownloadTopics);

    if (error) yield put(requestCustomerDownloadTopicsFailure(error));
    else yield put(requestCustomerDownloadTopicsSuccess(response));
}

export function* generateFetchCustomerDownloadTopicsWatcher() {
    yield takeLatest(FETCH_CUSTOMER_DOWNLOAD_TOPICS, fetchCustomerDownloadTopicsSaga);
}

/* Customer Downloads */
function* fetchCustomerDownloadsSaga(action: AnyAction): SagaIterator {
    yield put(requestCustomerDownloads());

    const { error, response } = yield call(fetchCustomerDownloads, action.typeName, action.pageable);

    if (error) yield put(requestCustomerDownloadsFailure(error));
    else {
        const content: unknown[] = [];
        let failState = false;

        // Foreach Download we also fetch the localized downloads
        for (const download of response.data.content) {
            // If there is no normalizedName add download and empty array for localized downloads
            if (!download.normalizedName) {
                content.push({ ...download, localizedDownloads: [] });
                continue;
            }

            let localizedError, localizedResponse;
            // TODO this is quick and dirty
            if (download.normalizedName.startsWith('PROMOTION_QR')) {
                let landingPageId = 'null';
                if (download.defaultResourceUrl != null) {
                    const splittedUrl = download.defaultResourceUrl.split('/');
                    landingPageId = splittedUrl[splittedUrl.length - 1];
                }
                // Fetch the localized downloads for this download
                const { error: localizedErrorInternal, response: localizedResponseInternal } = yield call(
                    fetchCustomerLocalizedDownloadsWithLandingpage,
                    download.normalizedName,
                    landingPageId
                );
                localizedError = localizedErrorInternal;
                localizedResponse = localizedResponseInternal;
            } else {
                // Fetch the localized downloads for this download
                const { error: localizedErrorInternal, response: localizedResponseInternal } = yield call(
                    fetchCustomerLocalizedDownloads,
                    download.normalizedName
                );
                localizedError = localizedErrorInternal;
                localizedResponse = localizedResponseInternal;
            }

            // If localized request failed stop looping over the rest and put failure
            if (localizedError) {
                failState = true;
                yield put(requestCustomerDownloadsFailure(error));
                break;
            }

            // Add the download with its localized downloads
            content.push({ ...download, localizedDownloads: localizedResponse.data });
        }

        // If all went well we put success
        if (!failState) {
            const patchedResponse = { ...response };
            patchedResponse.data.content = content;
            yield put(requestCustomerDownloadsSuccess(patchedResponse));
        }
    }
}

export function* generateFetchCustomerDownloadsWatcher() {
    yield takeLatest(FETCH_CUSTOMER_DOWNLOADS, fetchCustomerDownloadsSaga);
}

/* Customer Bills Info */
function* fetchCustomerBillsInfoSaga(action: AnyAction): SagaIterator {
    yield put(requestCustomerBillsInfo());

    const { error, response } = yield call(fetchCustomerBillsInfo);

    if (error) yield put(requestCustomerBillsInfoFailure(error));
    else yield put(requestCustomerBillsInfoSuccess(response));
}

export function* generateFetchCustomerBillsInfoWatcher() {
    yield takeLatest(FETCH_CUSTOMER_BILLS_INFO, fetchCustomerBillsInfoSaga);
}

/* Customer Bills */
function* fetchCustomerBillsSaga(action: AnyAction): SagaIterator {
    yield put(requestCustomerBills());

    const { error, response } = yield call(fetchCustomerBills, action.params, action.pageable);

    if (error) yield put(requestCustomerBillsFailure(error));
    else yield put(requestCustomerBillsSuccess(response));
}

export function* generateFetchCustomerBillsWatcher() {
    yield takeLatest(FETCH_CUSTOMER_BILLS, fetchCustomerBillsSaga);
}

/* Customer Commission Bills Info */
function* fetchCustomerCommissionBillsInfoSaga(action: AnyAction): SagaIterator {
    yield put(requestCustomerCommissionBillsInfo());

    const { error, response } = yield call(fetchCustomerCommissionBillsInfo);

    if (error) yield put(requestCustomerCommissionBillsInfoFailure(error));
    else yield put(requestCustomerCommissionBillsInfoSuccess(response));
}

export function* generateFetchCustomerCommissionBillsInfoWatcher() {
    yield takeLatest(FETCH_CUSTOMER_COMMISSION_BILLS_INFO, fetchCustomerCommissionBillsInfoSaga);
}

/* Customer Commission Bills */
function* fetchCustomerCommissionBillsSaga(action: AnyAction): SagaIterator {
    yield put(requestCustomerCommissionBills());

    const { error, response } = yield call(fetchCustomerCommissionBills, action.params, action.pageable);

    if (error) yield put(requestCustomerCommissionBillsFailure(error));
    else yield put(requestCustomerCommissionBillsSuccess(response));
}

export function* generateFetchCustomerCommissionBillsWatcher() {
    yield takeLatest(FETCH_CUSTOMER_COMMISSION_BILLS, fetchCustomerCommissionBillsSaga);
}
